// Blazor Server is loaded with autostart="false" (see public/index.html), so the SignalR
// circuit is NOT opened on public/auth pages. We start it exactly once, the first time the
// authenticated app shell mounts (PrivateRoutes — the only place the Blazor custom-element
// widgets are rendered). Gating on auth state rather than the URL is required because login
// is a soft, in-place React transition (no page reload), so the document and its already
// loaded blazor.server.js persist across the auth boundary.
//
// Starting the circuit installs Blazor's custom-element MutationObserver, which upgrades the
// registered kebab-case tags present at start AND any inserted later by lazy routes, so the
// dashboard/strategy/portfolio widgets render exactly as before.

declare global {
  interface Window {
    Blazor?: {start: () => Promise<void>}
  }
}

let started = false

export function ensureBlazorStarted(): void {
  if (started) {
    return
  }
  started = true
  // Авто-перезагрузка вкладки, когда circuit потерян безвозвратно. Типичный случай — РЕДЕПЛОЙ:
  // бэкенд перезапустился, сервер не знает старый circuit, и Blazor показывает РУЧНОЙ «Reload».
  // Полный reload подтягивает свежий index.html с новыми хэш-бандлами SPA — уходит боль
  // «старый фронт + новый бэк». Ставим ДО старта, чтобы поймать модалку, как только Blazor её создаст.
  setupAutoReloadOnCircuitLost()
  // Fire-and-forget: do not block React render on the circuit handshake.
  void window.Blazor?.start()
}

// Blazor Server переключает CSS-классы на элементе #components-reconnect-modal:
//   components-reconnect-rejected — сервер не знает circuit (после редеплоя): нужен полный reload;
//   components-reconnect-failed   — исчерпаны попытки реконнекта (сервер недоступен).
// В обоих случаях единственный выход — перезагрузка, поэтому делаем её автоматически (ровно один раз).
function setupAutoReloadOnCircuitLost(): void {
  let reloaded = false

  const reloadOnce = (delayMs: number): void => {
    if (reloaded) {
      return
    }
    reloaded = true
    window.setTimeout(() => window.location.reload(), delayMs)
  }

  const checkModal = (modal: Element): void => {
    if (modal.classList.contains('components-reconnect-rejected')) {
      // Редеплой: сервер поднялся, но старый circuit мёртв — перезагружаемся почти сразу.
      reloadOnce(250)
    } else if (modal.classList.contains('components-reconnect-failed')) {
      // Сервер ещё недоступен (рестарт идёт) — даём ему время подняться, затем reload.
      reloadOnce(3000)
    }
  }

  const watchModal = (modal: Element): void => {
    checkModal(modal)
    new MutationObserver(() => checkModal(modal)).observe(modal, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }

  const existing = document.getElementById('components-reconnect-modal')
  if (existing) {
    watchModal(existing)
    return
  }

  // Модалку реконнекта Blazor создаёт лениво — ждём её появления в DOM и тогда начинаем следить.
  const bodyObserver = new MutationObserver(() => {
    const modal = document.getElementById('components-reconnect-modal')
    if (modal) {
      bodyObserver.disconnect()
      watchModal(modal)
    }
  })
  bodyObserver.observe(document.body, {childList: true, subtree: true})
}
